import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { IDataContext } from '../../../core/component/list/component.interface';
import { SelectionItem } from '../../../core/component/select/select.component';
import { LocalDate } from '../../../core/model/local-date';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { AmortizationPlanPaymentNotice } from '../../model/amortization-plan';
import { AmortizationPlanService } from '../../service/data/amortization-plan.service';

@Component({
  standalone: false,
  selector: 'app-register-payment-notice-payment-form',
  templateUrl: './register-payment-notice-payment-form.component.html',
  styleUrls: ['./register-payment-notice-payment-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RegisterPaymentNoticePaymentFormComponent implements IDataContext, OnInit, OnDestroy {
  form: FormGroup;
  accountSelection: SelectionItem[] = [];
  working = false;

  private readonly _subscription = new Subscription();
  private _dataContext: AmortizationPlanPaymentNotice;

  constructor(
    private readonly _service: AmortizationPlanService,
    private readonly _modalService: ActionWindowService,
    private readonly _fb: FormBuilder,
    private readonly _fc: FormControlExFactory
  ) {
    this.form = this._fb.group({
      paymentNoticeId: this._fc.createHidden(''),
      amount: this._fc.createMandatory('REGISTER_PAYMENT.AMOUNT', null),
      date: this._fc.createMandatory('REGISTER_PAYMENT.DATE', LocalDate.today(), 'REGISTER_PAYMENT.HELP_TEXT.DATE'),
      companyBankAccountId: this._fc.createMandatory('REGISTER_PAYMENT.PAYMENT_METHOD', null),
    });
  }

  @Input() set dataContext(value: AmortizationPlanPaymentNotice) {
    this._dataContext = value;
    this.form.controls['paymentNoticeId'].setValue(value?.id ?? null);
    this.form.controls['amount'].setValue(value?.remainingAmount ?? value?.amount ?? null);
  }

  get dataContext(): AmortizationPlanPaymentNotice {
    return this._dataContext;
  }

  ngOnInit(): void {
    if (!this.dataContext?.id) {
      return;
    }

    this._subscription.add(
      this._service.getPaymentNoticePaymentModel(this.dataContext.id).subscribe(model => {
        this.form.controls['paymentNoticeId'].setValue(model.paymentNoticeId);
        this.form.controls['amount'].setValue(model.amount);
        this.form.controls['date'].setValue(model.date ?? LocalDate.today());
        this.form.controls['companyBankAccountId'].setValue(model.defaultCompanyBankAccountId);
        this.accountSelection = model.companyBankAccounts.map(account =>
          new SelectionItem(this.getBankAccountCaption(account), account.id)
        );
      })
    );
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  cancel(): void {
    this._modalService.close();
  }

  onSubmit(): void {
    if (this.form.invalid || this.working) {
      return;
    }

    this.working = true;
    this._subscription.add(
      this._service.registerPaymentNoticePayment(this.form.value).subscribe({
        next: () => {
          this._modalService.complete(true);
          this._modalService.close();
        },
        error: () => {
          this.working = false;
        },
      })
    );
  }

  private getBankAccountCaption(account: any): string {
    return [account.name, account.bankGiroNumber].filter(value => !!value).join(' - ');
  }
}
